'use client';
import { FlexWrapper } from '@intigral/shared';
import { useState, useEffect, useContext } from 'react';
import { useLocation } from 'react-router-dom';

import { Button, Text, RewardStep5 } from '@intigral/shared';
import { FlexChild } from '@intigral/shared/components/FlexWrapper';
import { get as getStorage, set } from '@intigral/utils/lib/browser-storage/localStorage';
import { STORE_NAME } from '@intigral/constant';
import { __ } from '../translations';
import { getUserInfo } from '../utils/user';
import { MixpanelContext } from '../contexts/MixpanelContext';
import { redirect } from '@intigral/utils/lib/helpers/browser';
import h from '../helpers/common';
import { useConsentCheck } from '../hooks/useConsentCheck';
import { useSelector } from 'react-redux';
import { IStore } from '../interfaces/StoreTypes';
import { useConfigurationSections } from '../hooks/useConfiguration';
import { Modal } from '../components/ui/Modal';

const NEW_USER_THRESHOLD_DAYS = 2; // Consider users as new if they registered within last 2 days

const GamificationWelcomeModal = () => {
    const [showModal, setShowModal] = useState(false);
    const [isNewUser, setIsNewUser] = useState(false);
    const selectedProfile = getStorage(STORE_NAME.SELECTED_PROFILE);
    const location = useLocation();
    const profile = useSelector((store: IStore) => store.profile);
    const { data: consentData, isLoading: consentLoading } = useConsentCheck(profile?.selectedProfile);

    // Get gamification configuration for dynamic icon
    // Using React Query instead of Redux for configuration
    const { gamificationConfigs = {} } = useConfigurationSections();

    const { track } = useContext(MixpanelContext) || {};

    const getRewardsUrl = () => {
        const currentLang = h.getLang();
        const rewardsPath = `/${currentLang}/rewards`;
        return rewardsPath;
    };

    // Get dynamic welcome modal icon from CMS configuration
    const getWelcomeModalIcon = () => {
        const welcomeModalConfig = (gamificationConfigs as any)?.welcomeModal;
        // Based on the actual structure: welcomeModal.imageUrls.welcome_modal_icon contains the URL directly
        if (welcomeModalConfig?.imageUrls?.welcome_modal_icon) {
            return welcomeModalConfig.imageUrls.welcome_modal_icon;
        }
        return null;
    };

    const dynamicIcon = getWelcomeModalIcon();

    const handleRewardsClick = () => {
        closeOverlay();
        redirect(getRewardsUrl());
    };

    // Make the function globally accessible for inline HTML
    useEffect(() => {
        // @ts-expect-error - Adding function to window object
        window.handleRewardsClick = (e) => {
            e.preventDefault();
            handleRewardsClick();
        };
        return () => {
            // @ts-expect-error - Removing function from window object
            delete window.handleRewardsClick;
        };
    }, []);

    useEffect(() => {
        const currentUser = getUserInfo();
        const firstLoginDate = currentUser?.firstLoginDate;

        // Check if modal has been dismissed for this specific profile
        const profileId = selectedProfile?.profileGuid;
        const hasBeenDismissed = profileId ? getStorage(`gamification_welcome_modal_dismissed_${profileId}`) : false;

        // Don't show modal if user is on cold-start, consent-management, or parental-pin-management pages
        const isOnColdStartPage = location.pathname.includes('cold-start');
        const isOnConsentManagementPage = location.pathname.includes('consent-management');
        const isOnParentalPinManagementPage = location.pathname.includes('parental-pin-management');

        // Don't show modal if consent is needed or if user is on restricted pages
        const shouldBlockModal =
            isOnColdStartPage ||
            isOnConsentManagementPage ||
            isOnParentalPinManagementPage ||
            consentLoading ||
            consentData?.consentNeeded;

        // Additional check: Don't show modal if it has been dismissed, regardless of showWelcomeScreen value
        // This prevents the modal from appearing again after profile data is refreshed from server
        if (selectedProfile && !shouldBlockModal && !hasBeenDismissed && selectedProfile?.showWelcomeScreen) {
            // timeout to avoid race condition
            setTimeout(() => {
                setShowModal(true);
            }, 3500);

            if (firstLoginDate) {
                const firstLogin = new Date(firstLoginDate);
                const now = new Date();
                const diffTime = Math.abs(now.getTime() - firstLogin.getTime());
                const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

                // Show modal only for new users (registered within last 2 days)
                if (diffDays <= NEW_USER_THRESHOLD_DAYS) {
                    setIsNewUser(true);
                }
            }
        } else {
            setShowModal(false);
        }
    }, [selectedProfile, location.pathname, consentData, consentLoading]);

    const closeOverlay = async () => {
        const selectedProfile = getStorage(STORE_NAME.SELECTED_PROFILE);
        if (selectedProfile) {
            const updatedProfile = {
                ...selectedProfile,
                showWelcomeScreen: false,
            };
            set(STORE_NAME.SELECTED_PROFILE, updatedProfile);
        }
        // Store dismissed status in localStorage for this specific profile
        const profileId = selectedProfile?.profileGuid;
        if (profileId) {
            set(`gamification_welcome_modal_dismissed_${profileId}`, true);
        }

        setShowModal(false);
    };

    return selectedProfile && showModal ? (
        <div className="container" style={{ paddingTop: 'var(--step)', paddingBottom: 'var(--step)' }}>
            <Modal
                useFlexLayout={false}
                open={showModal}
                onOpenChange={closeOverlay}
                alignSelf="bottom"
                closePlacement="null"
                widthSet="medium"
            >
                <FlexWrapper flexDirection="column" gap="3x">
                    <Text as="h2" variant="h2" margin="0">
                        {isNewUser
                            ? __('gamification_new_user_welcome_banner_title')
                            : __('gamification_exsisting_user_welcome_banner_title')}
                    </Text>
                    <FlexChild alignSelf="center">
                        {dynamicIcon ? (
                            <img
                                src={dynamicIcon}
                                alt="Gamification Welcome"
                                width={154}
                                height={160}
                                style={{ objectFit: 'contain' }}
                            />
                        ) : (
                            <RewardStep5 width={154} height={160} />
                        )}
                    </FlexChild>

                    <Text as="p" margin="0" color="gray">
                        {isNewUser
                            ? __('gamification_new_user_welcome_banner_description')
                            : __('gamification_exsisting_user_welcome_banner_description')}
                    </Text>
                    <Text
                        as="p"
                        margin="0"
                        color="gray"
                        dangerouslySetInnerHTML={{
                            __html: isNewUser
                                ? __('gamification_new_user_welcome_banner_guied')
                                      .replace('<color>', `<a  onclick="handleRewardsClick(event)">`)
                                      .replace('</color>', '</a>')
                                : __('gamification_exsisting_user_welcome_banner_guied')
                                      .replace('<color>', `<a  onclick="handleRewardsClick(event)">`)
                                      .replace('</color>', '</a>'),
                        }}
                    />
                    <Button primary onClick={closeOverlay}>
                        {isNewUser
                            ? __('gamification_new_user_welcome_banner_cta')
                            : __('gamification_exsisting_user_welcome_banner_cta')}
                    </Button>
                </FlexWrapper>
            </Modal>
        </div>
    ) : null;
};

export default GamificationWelcomeModal;
